/**
 * Per-user preferences: theme, density and saved table layouts (docs/04 §1b, §5,
 * ADR-23, ADR-64).
 *
 * Both live on the server, not in localStorage, because both are statements
 * about how a PERSON wants to work — "I want dense rows and these nine columns"
 * should be true on their laptop, the shared workstation and their phone, not
 * on whichever browser they happened to set it in.
 */

import {
  isDefaultLayout,
  resolveColumnOrder,
  resolvePageSize,
  resolveView,
  resolveVisibleColumns,
  type Density,
  type PageSize,
  type SavedLayouts,
  type TableId,
  type TableView,
  type ThemePreference,
} from '@propos/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { api } from './api.js';
import { applyTheme, watchDeviceTheme } from './theme.js';

export interface Preferences {
  readonly density: Density;
  readonly theme: ThemePreference;
  readonly layouts: SavedLayouts;
  /** Metric bars this person has collapsed, by bar id (punch item 2.1). */
  readonly collapsedBars: readonly string[];
}

const PREFERENCES_KEY = ['preferences'] as const;

export function usePreferences(): {
  readonly density: Density;
  readonly theme: ThemePreference;
  readonly layouts: SavedLayouts;
  readonly collapsedBars: readonly string[];
  readonly isPending: boolean;
} {
  const query = useQuery({
    queryKey: PREFERENCES_KEY,
    queryFn: () => api<Preferences>('/api/v1/me/preferences'),
  });
  return {
    density: query.data?.density ?? 'comfortable',
    theme: query.data?.theme ?? 'system',
    layouts: query.data?.layouts ?? {},
    collapsedBars: query.data?.collapsedBars ?? [],
    isPending: query.isPending,
  };
}

/**
 * One metric bar's collapsed state, persisted per user (punch item 2.1).
 *
 * The whole set is written on every toggle rather than a diff: it is a handful
 * of short ids, so a full write is simpler than a merge and cannot leave a
 * half-applied state. Optimistic, because a strip that keeps its old height for
 * half a second after you press Hide reads as a control that did not work.
 */
export function useMetricBarCollapse(barId: string): {
  readonly collapsed: boolean;
  readonly setCollapsed: (collapsed: boolean) => void;
} {
  const queryClient = useQueryClient();
  const { collapsedBars } = usePreferences();

  const save = useMutation<unknown, Error, readonly string[]>({
    mutationFn: (next) =>
      api('/api/v1/me/preferences', {
        method: 'PUT',
        body: JSON.stringify({ collapsedBars: next }),
      }),
    onMutate: async (next) => {
      await queryClient.cancelQueries({ queryKey: PREFERENCES_KEY });
      const previous = queryClient.getQueryData<Preferences>(PREFERENCES_KEY);
      queryClient.setQueryData<Preferences>(PREFERENCES_KEY, {
        density: previous?.density ?? 'comfortable',
        theme: previous?.theme ?? 'system',
        layouts: previous?.layouts ?? {},
        collapsedBars: [...next],
      });
      return { previous };
    },
    onSettled: () => void queryClient.invalidateQueries({ queryKey: PREFERENCES_KEY }),
  });

  return {
    collapsed: collapsedBars.includes(barId),
    setCollapsed: (collapsed) => {
      const without = collapsedBars.filter((id) => id !== barId);
      save.mutate(collapsed ? [...without, barId] : without);
    },
  };
}

/**
 * Stamp the server's answer onto <html>, once the shell knows it.
 *
 * Split from usePreferences so the many callers that only want density do not
 * each register a media listener: this hook is mounted exactly once, by the
 * shell. Until it runs, main.tsx's cached replay is what the page is showing —
 * so the common case is that this confirms what is already on screen.
 */
export function useApplyTheme(): void {
  const { theme, isPending } = usePreferences();
  useEffect(() => {
    // Not while the query is in flight: the fallback 'system' would clear an
    // explicit choice the cached replay had correctly applied, producing the
    // flash the cache exists to prevent.
    if (isPending) return undefined;
    applyTheme(theme);
    return watchDeviceTheme(theme);
  }, [theme, isPending]);
}

export function useSetTheme(): ReturnType<typeof useMutation<unknown, Error, ThemePreference>> {
  const queryClient = useQueryClient();
  return useMutation<unknown, Error, ThemePreference>({
    mutationFn: (next) =>
      api('/api/v1/me/preferences', { method: 'PUT', body: JSON.stringify({ theme: next }) }),
    onMutate: async (next) => {
      await queryClient.cancelQueries({ queryKey: PREFERENCES_KEY });
      const previous = queryClient.getQueryData<Preferences>(PREFERENCES_KEY);
      queryClient.setQueryData<Preferences>(PREFERENCES_KEY, {
        density: previous?.density ?? 'comfortable',
        theme: next,
        layouts: previous?.layouts ?? {},
        collapsedBars: previous?.collapsedBars ?? [],
      });
      // Repainting the whole app is the point of this control; waiting for the
      // round-trip to do it would make the choice feel broken.
      applyTheme(next);
      return { previous };
    },
    onSettled: () => void queryClient.invalidateQueries({ queryKey: PREFERENCES_KEY }),
  });
}

export function useSetDensity(): ReturnType<typeof useMutation<unknown, Error, Density>> {
  const queryClient = useQueryClient();
  return useMutation<unknown, Error, Density>({
    mutationFn: (next) =>
      api('/api/v1/me/preferences', { method: 'PUT', body: JSON.stringify({ density: next }) }),
    // Your own change appears instantly (docs/03 §2).
    onMutate: async (next) => {
      await queryClient.cancelQueries({ queryKey: PREFERENCES_KEY });
      const previous = queryClient.getQueryData<Preferences>(PREFERENCES_KEY);
      queryClient.setQueryData<Preferences>(PREFERENCES_KEY, {
        density: next,
        theme: previous?.theme ?? 'system',
        layouts: previous?.layouts ?? {},
        collapsedBars: previous?.collapsedBars ?? [],
      });
      return { previous };
    },
    onSettled: () => void queryClient.invalidateQueries({ queryKey: PREFERENCES_KEY }),
  });
}

export interface TableLayoutControl {
  /** Visible column ids in catalog order, locked columns included. */
  readonly visible: readonly string[];
  /** The same ids in the order the user chose (X-6.2's sibling). */
  readonly order: readonly string[];
  /** Chosen pixel widths by column id; a column absent sizes itself. */
  readonly widths: Readonly<Record<string, number>>;
  readonly view: TableView;
  readonly pageSize: PageSize;
  readonly isDefault: boolean;
  readonly isSaving: boolean;
  readonly setColumns: (columns: readonly string[]) => void;
  readonly setView: (view: TableView) => void;
  readonly setOrder: (order: readonly string[]) => void;
  readonly setWidth: (columnId: string, width: number) => void;
  readonly setPageSize: (size: PageSize) => void;
  readonly reset: () => void;
}

/**
 * One table's column choice. Rendering must not wait on the round-trip — the
 * checkbox flipping half a second after the click is the difference between a
 * control that feels real and one that feels broken — so the cache is updated
 * first and the save follows.
 */
export function useTableLayout(tableId: TableId): TableLayoutControl {
  const queryClient = useQueryClient();
  const { layouts } = usePreferences();

  const saved = layouts[tableId];

  /**
   * The whole layout is written every time, columns and view together, because
   * the endpoint stores one object per table. Writing only the field that
   * changed would drop the other one.
   */
  interface LayoutWrite {
    readonly columns: readonly string[];
    readonly view: TableView;
    readonly order?: readonly string[] | undefined;
    readonly widths?: Readonly<Record<string, number>> | undefined;
    readonly pageSize?: PageSize | undefined;
  }

  const save = useMutation<unknown, Error, LayoutWrite>({
    mutationFn: (layout) =>
      api(`/api/v1/me/preferences/layouts/${tableId}`, {
        method: 'PUT',
        body: JSON.stringify(layout),
      }),
    onMutate: async (layout) => {
      await queryClient.cancelQueries({ queryKey: PREFERENCES_KEY });
      const previous = queryClient.getQueryData<Preferences>(PREFERENCES_KEY);
      queryClient.setQueryData<Preferences>(PREFERENCES_KEY, {
        density: previous?.density ?? 'comfortable',
        theme: previous?.theme ?? 'system',
        collapsedBars: previous?.collapsedBars ?? [],
        layouts: {
          ...previous?.layouts,
          [tableId]: {
            columns: [...layout.columns],
            view: layout.view,
            ...(layout.order === undefined ? {} : { order: [...layout.order] }),
            ...(layout.widths === undefined ? {} : { widths: { ...layout.widths } }),
            ...(layout.pageSize === undefined ? {} : { pageSize: layout.pageSize }),
          },
        },
      });
      return { previous };
    },
    onSettled: () => void queryClient.invalidateQueries({ queryKey: PREFERENCES_KEY }),
  });

  const currentView = resolveView(saved);
  const currentOrder = resolveColumnOrder(tableId, saved);
  const currentWidths = saved?.widths ?? {};
  const currentPageSize = resolvePageSize(saved?.pageSize);

  /**
   * Every write sends the WHOLE layout, because the endpoint stores one object
   * per table: writing only the field that changed would drop the others. That
   * is also why each setter reads the current values above rather than trusting
   * a caller to pass them.
   */
  const write = (change: Partial<LayoutWrite>): void => {
    save.mutate({
      columns: resolveVisibleColumns(tableId, saved),
      view: currentView,
      ...(saved?.order === undefined ? {} : { order: currentOrder }),
      ...(saved?.widths === undefined ? {} : { widths: currentWidths }),
      ...(saved?.pageSize === undefined ? {} : { pageSize: currentPageSize }),
      ...change,
    });
  };

  return {
    visible: resolveVisibleColumns(tableId, saved),
    order: currentOrder,
    widths: currentWidths,
    view: currentView,
    pageSize: currentPageSize,
    isDefault: isDefaultLayout(tableId, saved),
    isSaving: save.isPending,
    setColumns: (columns) => {
      write({ columns });
    },
    setView: (view) => {
      write({ view });
    },
    setOrder: (order) => {
      write({ order });
    },
    setWidth: (columnId, width) => {
      write({ widths: { ...currentWidths, [columnId]: width } });
    },
    setPageSize: (size) => {
      write({ pageSize: size });
    },
    reset: () => {
      // Resetting stores the default set explicitly rather than deleting the
      // key: an absent layout means "never chose", and a user who resets HAS
      // chosen — if the catalog's defaults later change, their reset should not
      // silently move with them. It also clears order and widths, which are
      // part of "how this table looks"; the view and page size are separate
      // choices about shape and volume, and survive.
      save.mutate({
        columns: resolveDefaults(tableId),
        view: currentView,
        ...(saved?.pageSize === undefined ? {} : { pageSize: currentPageSize }),
      });
    },
  };
}

function resolveDefaults(tableId: TableId): readonly string[] {
  return resolveVisibleColumns(tableId, undefined);
}
